import React, { useState, memo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "./HeroSection.css";
import { motion } from "framer-motion";
import Typewriter from "./Typewriter";

const DROPDOWN_CLOSE_DELAY = 350;

const HERO_IMAGES = [
  "/images/homepge/02_51_SPEEHUIS_SpeeArchitecten.jpg",
  "/images/homepge/08_64_SPEEHUIS_SpeeArchitecten.jpg",
  "/images/homepge/76898a_0687d69f0fc143dab2af94d1edd01d44~mv2.avif",
  "/images/homepge/76898a_a288c77c39854bcba4bc432475bae859~mv2.avif",
];

const HERO_IMAGE_INTERVAL_MS = 5500;

const HeroSection = memo(() => {
  const [showSubheading, setShowSubheading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null); // 'grad' | 'braun' | null
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownCloseTimer = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setHeroImageIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, HERO_IMAGE_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (menuOpen && window.matchMedia("(max-width: 768px)").matches) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleTypewriterDone = useCallback(() => {
    setShowSubheading(true);
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const openDropdown = useCallback((which) => {
    if (dropdownCloseTimer.current) {
      clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
    setDropdownOpen(which);
  }, []);

  const closeDropdown = useCallback(() => {
    dropdownCloseTimer.current = setTimeout(() => {
      setDropdownOpen(null);
      dropdownCloseTimer.current = null;
    }, DROPDOWN_CLOSE_DELAY);
  }, []);

  const toggleMobileDropdown = useCallback((which) => {
    if (dropdownCloseTimer.current) {
      clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
    setDropdownOpen((prev) => (prev === which ? null : which));
  }, []);

  const gradSubmenu = [
    { to: "/izdelki/grad-sistem", label: "Več o Grad sistem" },
    { to: "/izdelki/terasne-obloge", label: "Terasne obloge" },
    { to: "/izdelki/fasadne-obloge", label: "Fasadne obloge" },
    { to: "/izdelki/notranje-obloge", label: "Notranje obloge" },
    { to: "/izdelki/stropne-obloge", label: "Stropne obloge" },
    { to: "/izdelki/ograje-vrata", label: "Obloge za ograje in vrata" },
    { to: "/izdelki/keramika", label: "Keramika" },
  ];
  const braunSubmenu = [
    { to: "/izdelki/braun-sistem", label: "Več o Gerhardt Braun" },
    { to: "/izdelki/predelne-stene", label: "Predelne stene" },
    { to: "/izdelki/ograje", label: "Konstrukcije" },
    { to: "/izdelki/nadstreški", label: "Nadstreški" },
    { to: "/izdelki/boksi", label: "Boksi" },
  ];

  const navContent = (
    <>
      <button
        type="button"
        className="hero-mobile-close"
        onClick={closeMenu}
        aria-label="Zapri meni"
      >
        × Zapri
      </button>
      <a href="#home" onClick={closeMenu}>Domov</a>
      <a href="#about" onClick={closeMenu}>O nas</a>
      <div
        className={`nav-dropdown ${dropdownOpen === "braun" ? "nav-dropdown-open" : ""}`}
        onMouseEnter={!isMobile ? () => openDropdown("braun") : undefined}
        onMouseLeave={!isMobile ? closeDropdown : undefined}
      >
        <button
          type="button"
          className="nav-dropdown-trigger"
          onClick={(e) => {
            if (isMobile) {
              e.preventDefault();
              e.stopPropagation();
            }
            toggleMobileDropdown("braun");
          }}
          aria-expanded={dropdownOpen === "braun"}
        >
          Gerhardt Braun <span className="nav-dropdown-chevron">›</span>
        </button>
        <div className="nav-dropdown-menu">
          {braunSubmenu.map(({ to, label }) => (
            <Link key={label} to={to} onClick={closeMenu}>{label}</Link>
          ))}
        </div>
      </div>
      <div
        className={`nav-dropdown ${dropdownOpen === "grad" ? "nav-dropdown-open" : ""}`}
        onMouseEnter={!isMobile ? () => openDropdown("grad") : undefined}
        onMouseLeave={!isMobile ? closeDropdown : undefined}
      >
        <button
          type="button"
          className="nav-dropdown-trigger"
          onClick={(e) => {
            if (isMobile) {
              e.preventDefault();
              e.stopPropagation();
            }
            toggleMobileDropdown("grad");
          }}
          aria-expanded={dropdownOpen === "grad"}
        >
          Grad sistem <span className="nav-dropdown-chevron">›</span>
        </button>
        <div className="nav-dropdown-menu">
          {gradSubmenu.map(({ to, label }) => (
            <Link key={label} to={to} onClick={closeMenu}>{label}</Link>
          ))}
        </div>
      </div>
      <a href="#kaj-nas-locuje" onClick={closeMenu}>Kaj nas ločuje</a>
      <a href="#katalogi" onClick={closeMenu}>Katalogi</a>
      <a href="#contact" onClick={closeMenu}>Kontakt</a>
    </>
  );

  return (
    <motion.section
      id="home"
      className="hero-section hero-fullscreen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Fullscreen background – izmenjava slik iz homepge, začetek s Speehuis */}
      <div className="hero-background-image">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className="hero-bg-img"
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            style={{ opacity: i === heroImageIndex ? 1 : 0 }}
          />
        ))}
        <div className="hero-background-overlay" />
      </div>

      {/* Content overlay */}
      <div className="hero-content-wrapper">
        <div className={`hero-top-bar ${menuOpen ? "hero-nav-open" : ""}`}>
          <a href="#home" className="hero-business-logo-link" aria-label="Domov">
            <img
              src="/images/LOGO-large-white-1536x489.png"
              alt="Orsan Logo"
              className="hero-business-logo"
              loading="eager"
              decoding="async"
            />
          </a>
          <button
            type="button"
            className="hero-nav-toggle"
            aria-label={menuOpen ? "Zapri meni" : "Odpri meni"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span className="hero-nav-toggle-bar" />
            <span className="hero-nav-toggle-bar" />
            <span className="hero-nav-toggle-bar" />
          </button>
          {menuOpen && isMobile ? (
            createPortal(
              <nav className="hero-nav-overlay hero-nav-overlay-portal" aria-label="Navigacija">
                {navContent}
              </nav>,
              document.body
            )
          ) : (
            <motion.nav
              className="hero-nav-overlay"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
            >
              {navContent}
            </motion.nav>
          )}
        </div>

        <div className="hero-main-content">
          <motion.div
            className="hero-text-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          >
            <h1 className="hero-main-heading">
              <Typewriter
                text="Modularni sistemi za skladiščne rešitve, predelne stene, terasne in fasadne obloge ter zunanje konstrukcije"
                speed={18}
                onDone={handleTypewriterDone}
              />
            </h1>
            {showSubheading && (
              <motion.p
                className="hero-subheading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                Specializirani za izdelavo in montažo kletnih shramb, predelnih
                sten, terasnih in fasadnih oblog, nadstreškov in fasadnih sistemov. Od
                kakovostnih materialov do popolne montaže – vse iz enih rok.
              </motion.p>
            )}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
});

HeroSection.displayName = "HeroSection";

export default HeroSection;
